/**
 * Minimal markdown renderer — no external dependencies.
 *
 * Supported syntax:
 *   **bold**  _italic_  ~~strikethrough~~  `inline code`
 *   ```code block```
 *   > blockquote
 *   - / * bullet list
 *   1. numbered list
 *   [text](url) links
 *
 * Security: all input is HTML-escaped BEFORE markdown patterns are
 * applied. The only raw HTML we inject is the trusted structural tags
 * we build ourselves — no user content ever ends up un-escaped inside
 * an attribute value or tag body.
 */

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

/**
 * Render `text` as markdown and return an HTML string safe for
 * `dangerouslySetInnerHTML`.
 */
export function renderMarkdown(text: string): string {
    if (!text) return "";

    const lines = text.split(/\r?\n/);
    const out: string[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i]!;

        // ── Fenced code block (``` ... ```) ─────────────────────────────
        if (/^```/.test(line)) {
            const codeLang = line.slice(3).trim();
            const codeLines: string[] = [];
            i++;
            while (i < lines.length && !/^```/.test(lines[i]!)) {
                codeLines.push(lines[i]!);
                i++;
            }
            i++; // consume closing ```
            const langAttr = codeLang ? ` class="language-${escapeHtml(codeLang)}"` : "";
            out.push(
                `<pre class="rounded bg-muted px-3 py-2 text-xs font-mono overflow-x-auto my-1"><code${langAttr}>${escapeHtml(codeLines.join("\n"))}</code></pre>`
            );
            continue;
        }

        // ── Blockquote ───────────────────────────────────────────────────
        if (/^> /.test(line)) {
            const quoteLines: string[] = [];
            while (i < lines.length && /^> /.test(lines[i]!)) {
                quoteLines.push(lines[i]!.slice(2));
                i++;
            }
            const inner = renderMarkdown(quoteLines.join("\n"));
            out.push(
                `<blockquote class="border-l-4 border-muted-foreground/40 pl-3 text-muted-foreground my-1">${inner}</blockquote>`
            );
            continue;
        }

        // ── Unordered list ───────────────────────────────────────────────
        if (/^[*\-] /.test(line)) {
            const items: string[] = [];
            while (i < lines.length && /^[*\-] /.test(lines[i]!)) {
                items.push(lines[i]!.slice(2));
                i++;
            }
            out.push(
                `<ul class="list-disc pl-5 my-1 space-y-0.5">${items
                    .map((it) => `<li>${renderInline(it)}</li>`)
                    .join("")}</ul>`
            );
            continue;
        }

        // ── Ordered list ─────────────────────────────────────────────────
        if (/^\d+\. /.test(line)) {
            const items: string[] = [];
            while (i < lines.length && /^\d+\. /.test(lines[i]!)) {
                items.push(lines[i]!.replace(/^\d+\. /, ""));
                i++;
            }
            out.push(
                `<ol class="list-decimal pl-5 my-1 space-y-0.5">${items
                    .map((it) => `<li>${renderInline(it)}</li>`)
                    .join("")}</ol>`
            );
            continue;
        }

        // ── Horizontal rule ──────────────────────────────────────────────
        if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
            out.push(`<hr class="my-2 border-border" />`);
            i++;
            continue;
        }

        // ── Empty line = paragraph break ─────────────────────────────────
        if (line.trim() === "") {
            out.push("<br />");
            i++;
            continue;
        }

        // ── Plain paragraph ──────────────────────────────────────────────
        out.push(`<p class="my-0.5">${renderInline(line)}</p>`);
        i++;
    }

    return out.join("");
}

/**
 * Render inline markdown within a single line. The input is first
 * HTML-escaped so no user characters survive as raw HTML.
 */
function renderInline(text: string): string {
    // Escape first, then apply markdown patterns that re-introduce safe HTML.
    let s = escapeHtml(text);

    // Bold+italic: ***text*** or ___text___
    s = s.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
    s = s.replace(/___(.+?)___/g, "<strong><em>$1</em></strong>");

    // Bold: **text** or __text__
    s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/__(.+?)__/g, "<strong>$1</strong>");

    // Italic: *text* or _text_ (not preceded/followed by space)
    s = s.replace(/(?<![*_])\*([^*]+?)\*(?![*_])/g, "<em>$1</em>");
    s = s.replace(/(?<![*_])_([^_]+?)_(?![*_])/g, "<em>$1</em>");

    // Strikethrough: ~~text~~
    s = s.replace(/~~(.+?)~~/g, "<del>$1</del>");

    // Inline code: `text`
    s = s.replace(/`([^`]+)`/g,
        `<code class="rounded bg-muted px-1 font-mono text-[0.85em]">$1</code>`);

    // Links: [text](url) — url was already HTML-escaped by escapeHtml
    s = s.replace(
        /\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g,
        `<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary underline">$1</a>`
    );

    // Plain URLs — linkify bare https?:// that aren't inside an <a> already.
    // We check for the absence of '"' ahead to avoid double-linking.
    s = s.replace(
        /(?<![">])(https?:\/\/[^\s<>"]+)/g,
        `<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary underline">$1</a>`
    );

    return s;
}

/**
 * Strip markdown syntax from `text` and return plain text — used for
 * notification previews where HTML is not permitted.
 */
export function stripMarkdown(text: string): string {
    return text
        .replace(/```[\s\S]*?```/g, "[code]")
        .replace(/`[^`]+`/g, "")
        .replace(/\*\*\*(.+?)\*\*\*/g, "$1")
        .replace(/___(.+?)___/g, "$1")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/__(.+?)__/g, "$1")
        .replace(/\*([^*]+?)\*/g, "$1")
        .replace(/_([^_]+?)_/g, "$1")
        .replace(/~~(.+?)~~/g, "$1")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/^> /gm, "")
        .replace(/^[*\-] /gm, "")
        .replace(/^\d+\. /gm, "")
        .trim();
}
